import faIR from 'antd/lib/locale/fa_IR';
import { jwtDecode } from 'jwt-decode';
import { useTranslation } from 'react-i18next';
import { EditOutlined } from '@ant-design/icons';
import { ReloadOutlined } from '@ant-design/icons';
import { motion, AnimatePresence } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Row, Col, Input, ConfigProvider, Button, Tooltip, App } from 'antd';
import { storage } from '@/utils';
import { useAnnouncementStore } from '@/store';
import { Flex, UniversityLogo } from '@/components';
import { LoadingSpin } from '@/components';
import useFormLogin, { InitValues } from '@/hooks/app/useFormLogin';
import { getUniversityWithFlattedUrl } from '@/utils/universityUtils';
import { useLocalStorage, useModal, useDevice } from '@/hooks';
import { envConfig, NON_MANAGEMENT_ROLES } from '@/configs';
import { useUserContext, useUniversityContext } from '@/context';
import { SelectUniversityModal, ChooseSystemModal } from '@/components/modals';
import { ReactComponent as SamadSVG } from '@/assets/images/logo-samad-colored.svg';
import LoginAnnouncements from './LoginAnnouncements';
import background from '@/assets/images/login-bg.jpg';
import classNames from 'classnames';
import { IoIosArrowUp } from 'react-icons/io';
import { gererateCaptcha } from "@/services/user.service";

import './styles.scss';
import ResetPassword from '../ResetPassword/ResetPassword';
import { useMutation } from '@tanstack/react-query';
import uuidv4 from '@/utils/uuidGenerator';

const isOnPremise = ['on-promise', 'on-premise'].includes(envConfig.serveMode);

export function LoginPage() {

  const [t] = useTranslation(['common']);
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUserContext();
  const { message } = App.useApp();


  const { university, setUniversity, loading: universityLoading, universities } = useUniversityContext();
  const [announcements, fetchAnnouncements, announcementsLoading] = useAnnouncementStore(s => [s.announcements, s.fetchAnnouncements, s.loading]);

  const { isDesktop } = useDevice();
  const universityModal = useModal();
  const chooseSystemModal = useModal();
  const [loading, setLoading] = useState(false);

  const [showBackTop, setShowBackTop] = useState(false);
  const [showingCas, setShowingCas] = useLocalStorage<string>('UNIVERSITY_CAS', true as any);
  const contentContainerRef = useRef<HTMLDivElement>();

  const [showLogin, setShowLogin] = useState(true);
  const [uuid, setUuid] = useState(uuidv4());

  console.log('university', university);
  

  const { mutate: getCaptcha, isPending: isCaptchaPending, data: captcha } = useMutation({
    mutationKey: ['/rest/captcha', uuid],
    mutationFn: async () => (await gererateCaptcha({ nonce: uuid })),
  });

  useEffect(() => {
    getCaptcha();
  }, [uuid]);

  const onUniSelect = useCallback((uniKey: string) => {
    const uni = universities?.find(u => u.unikey === uniKey);
    if (uni) {
      setUniversity(getUniversityWithFlattedUrl(uni));
    }
  }, [universities, fetchAnnouncements, setUniversity]);

  const onSubmit = useCallback(async ({ username, password, captchaText }: InitValues) => {
    const founded = universities?.find(newUni => newUni.unikey === university.unikey);
    setUniversity(getUniversityWithFlattedUrl(founded));
    setLoading(true);
    const opts = { duration: 2, style: { marginTop: '2rem' } };
    let success = undefined;
    let status = undefined;
    let errResponse = undefined;
    await user.login({ username, password, captchaText, validation: captcha?.data?.payload?.validation, nonce: uuid })
      .then(() => success = true)
      .catch((e) => {
        console.error({ e });
        success = false;
        status = e?.response?.status;
        errResponse = e?.response?.data;
      });
    setTimeout(() => {
      if (success) message.success({ ...opts, content: t('common:messages.welcome') });
      else {
        setUuid(uuidv4());
        if (status === 400 || status === 401) {
          const errDesc = String(errResponse?.error_description ?? '');
          if (errDesc.includes('User account is locked')) {
            message.error({ ...opts, content: t('common:errors.account-locked-by-operator') });
          }
          else if (errDesc?.includes('User is disabled')) {
            message.error({ ...opts, content: t('common:errors.account-disabled') });
          }
          else if (errDesc?.includes('حساب')) {
            message.error({ ...opts, content: errDesc });
          }
          else {
            message.error({ ...opts, content: t('common:errors.invalid-credentials') });
          }
        }
        else if (status != null && status >= 400) {
          message.error({ ...opts, content: t('common:errors.auth-connect-code', { code: status }) });
        }
        else {
          message.error({ ...opts, content: t('common:errors.auth-connect') });
        }
      }
      setLoading(false);
    }, 1500);
  }, [universities, university, message, t, user, setUniversity, captcha]);





  useEffect(() => {
    if (user.isAuth) {
      const search = new URLSearchParams(location.search);
      const nextUrl = search.get('next');
      const from = { pathname: nextUrl?.startsWith('/') ? nextUrl : '/' };
      let nonStudent = false;
      try {
        const decoded = jwtDecode(storage.get('TOKEN', null).toString()) as any;
        for (const item of decoded?.authorities) {
          if (!NON_MANAGEMENT_ROLES.includes(item)) {
            nonStudent = true;
            break;
          }
        }
      }
      catch (e) {
      }
      setTimeout(() => {
        let showModalOption = null;
        if (isOnPremise) {
          showModalOption = envConfig?.onPremise?.core?.showModalOnLogin;
        }
        else {
          showModalOption = (university?.showModalOnLogin ?? true);
        }

        if (nonStudent && showModalOption) {
          return chooseSystemModal.show({ url: from });
        }
        navigate(from, { replace: true });
      }, loading ? 1000 : 0);
    }
  }, [university, user.isAuth, loading, location, navigate, chooseSystemModal]);

  useEffect(() => {
    fetchAnnouncements();
  }, [university?.unikey]);

  useEffect(() => {
    // Check for redirect from old samad
    if (document.referrer) {
      if (universities == null) return;
      const host = new URL(document.referrer).hostname;
      if (host === 'localhost') return;
      const found = universities?.find(u => {
        const url = getUniversityWithFlattedUrl(u).universityURL;
        try {
          return (new URL(url)).hostname === host;
        }
        catch (e) { }
      });
      if (found) {
        setUniversity(getUniversityWithFlattedUrl(found));
      }
    }
    else if (university == null || !university?.unikey) {
      universityModal.show();
    }
  }, [universities, universityModal, university, setUniversity]);

  const handleCasClick = useCallback(() => {
    window.location.href = `${university?.casUrl}&pwa=true`;
  }, [university]);

  const formik = useFormLogin(onSubmit);
  const { values } = formik;

  const hasCas = useMemo(() => university?.casUrl !== '' && university?.casUrl != null, [university]);

  useEffect(() => {
    if (hasCas && university?.forceCas && !showingCas) {
      setShowingCas(true as any);
    }
  }, [hasCas, university, showingCas]);

  useEffect(() => {
    const element = contentContainerRef.current;
    if (!element) return;
    const onScroll = () => {
      setShowBackTop(element.scrollTop > 20);
    };
    element.addEventListener('scroll', onScroll);
    return () => element.removeEventListener('scroll', onScroll);
  }, []);


  const switchToRecoverPassword = (isLogin: boolean) => {
    setShowLogin(isLogin);
  }

  return (
    <ConfigProvider locale={faIR} direction="rtl">
      <ChooseSystemModal {...chooseSystemModal} />
      {!isOnPremise &&
        <SelectUniversityModal
          onChange={onUniSelect}
          universities={universities}
          {...universityModal} />
      }
      <div className={classNames('app-login-container', { 'app-login-container-mobile': !isDesktop() })}>
        <div className="login-brand-logo-mobile">
          {!envConfig.ui.removeCorpInfo &&
            <>
              <UniversityLogo
                style={{ maxHeight: '60px' }}
                uniKey={university?.unikey} />
              <SamadSVG className="my-auto ms-auto" style={{ maxHeight: '60px' }} />
            </>
          }
        </div>
        <Row ref={contentContainerRef} className="app-login-content-container">
          <Col className="app-login-sidebar" lg={8} span={24}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: '0.5', delay: 0.5 }}
              className="flex flex-col w-full h-full">
              {!envConfig.ui.removeCorpInfo &&
                <div className="login-brand-logo-desktop">
                  <UniversityLogo uniKey={university?.unikey} />
                  <SamadSVG className="my-auto ms-auto" />
                </div>
              }
              {showLogin ?
                <>
                  <Flex
                    align="center"
                    justify="center"
                    direction="column"
                    className="sidebar-title">
                    {t('common:messages.login-title')}
                  </Flex>
                  <Flex
                    align="center"
                    justify="center"
                    direction="column"
                    className="sidebar-description">
                    {t('common:messages.login-description')}
                  </Flex>
                  <Flex direction="column" gap="1rem">
                    <Link
                      target="_blank"
                      to={(envConfig.guideLink ?? '').trim() === '' ? '#' : envConfig.guideLink}>
                      <Flex className="text-primary-dark" align="center">
                        <QuestionCircleOutlined className="text-lg" />
                        <span>{t('common:labels.samad-guide')}</span>
                      </Flex>
                    </Link>
                    {!isOnPremise && <Flex className="relative" direction="column">
                      <Input
                        disabled
                        spellCheck="false"
                        className="auth-input"
                        value={university?.universityLabel}
                        placeholder={t('common:messages.select-uni')} />
                      {isDesktop() ?
                        <Tooltip title={t('common:labels.select-university')}>
                          <div onClick={universityModal.show} className="input-btn-edit">
                            <EditOutlined />
                          </div>
                        </Tooltip> :
                        <div onClick={universityModal.show} className="input-btn-edit">
                          <EditOutlined />
                        </div>
                      }
                    </Flex>}
                    {hasCas && showingCas ? (
                      <>
                        <p className="rtl !text-start">
                          {t('common:messages.sso-hint')}
                        </p>
                        <Button
                          className="auth-btn mb-0"
                          loading={loading}
                          onClick={handleCasClick}>
                          {t('common:nav.login-sm')}
                        </Button>
                        {!university?.forceCas &&
                          <Flex
                            inlineFlex
                            component="b"
                            onClick={() => setShowingCas(false as any)}
                            className="justify-center text-center text-xs cursor-pointer text-primary-dark">
                            {t('common:nav.login-user-pass')}
                          </Flex>
                        }
                      </>
                    ) :
                      <>
                        <Flex direction="column">
                          <Input
                            name="username"
                            spellCheck="false"
                            className="auth-input"
                            value={values.username}
                            onChange={formik.handleChange}
                            placeholder={t('common:labels.username')}
                            onKeyDown={e => e.key === 'Enter' ? formik.submitForm() : undefined}
                          />
                        </Flex>
                        <Flex>
                          <Input.Password
                            name="password"
                            spellCheck="false"
                            className="auth-input"
                            value={values.password}
                            onChange={formik.handleChange}
                            placeholder={t('common:labels.password')}
                            onKeyDown={e => e.key === 'Enter' ? formik.submitForm() : undefined}
                          />
                        </Flex>
                        {
                          university?.captchaEnable > 0 &&
                          <Flex>
                            <Input
                              name="captchaText"
                              spellCheck="false"
                              className="auth-input w-1/2 "
                              value={values.captchaText}
                              onChange={formik.handleChange}
                              placeholder={t('common:labels.captcha')}
                              onKeyDown={e => e.key === 'Enter' ? formik.submitForm() : undefined}
                            />
                            <Flex className='w-1/2'>
                              {
                                isCaptchaPending ? <LoadingSpin className="text-primary" text="" /> :
                                  <img src={`data:image/png;base64,${captcha?.data?.payload?.image || ''}`} alt="captcha" className="w-[110px]" />
                              }
                              <Button onClick={() => getCaptcha()}><ReloadOutlined /></Button>
                            </Flex>
                          </Flex>
                        }
                        <Button
                          className="auth-btn mb-0"
                          loading={loading}
                          onClick={formik.submitForm}
                          disabled={
                            !university || university.universityURL === '' ||
                            universityLoading || !formik.isValid
                          }>{t('common:nav.login-sm')}</Button>
                        {
                          envConfig.onPremise.core.auth.resetPassword &&
                          <Flex
                            inlineFlex
                            component="b"
                            className="justify-center text-center text-xs cursor-pointer  text-primary-dark"
                            onClick={() => switchToRecoverPassword(false)}>
                            {t('common:labels.recover-password')}

                          </Flex>
                        }
                        {hasCas &&
                          <Flex
                            inlineFlex
                            component="b"
                            onClick={() => setShowingCas(true as any)}
                            className="justify-center text-center text-xs cursor-pointer text-primary-dark">
                            {t('common:labels.login-sso')}
                          </Flex>
                        }
                      </>
                    }
                  </Flex>
                </> :
                <ResetPassword changePage={switchToRecoverPassword} />
              }
            </motion.div>
          </Col>
          <Col className="app-login-content" lg={16} span={24}>
            <img className="image-background" src={background} alt="image-background" />
            <Flex className="content-overlay" direction="column">
              <AnimatePresence mode="wait">
                {showBackTop &&
                  <motion.div
                    key="btn-back-top"
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.5 }}
                    className="btn-back-top"
                    onClick={() => contentContainerRef.current.scroll({ top: 0, behavior: 'smooth' })}
                  >
                    <IoIosArrowUp />
                  </motion.div>
                }
              </AnimatePresence>
              <AnimatePresence mode="wait">
                {university?.unikey != '' ?
                  <LoginAnnouncements
                    key="announcements"
                    announcements={announcements}
                    loading={announcementsLoading}
                  /> :
                  <motion.div
                    key="slogan"
                    initial={{ opacity: 0, x: '-200px' }}
                    animate={{ opacity: 1, x: '0' }}
                    exit={{ opacity: 0, x: '-200px', transition: { delay: 0, duration: 0.25 } }}
                    transition={{ duration: 0.5, delay: 0.5, bounce: false }}
                    className="name-desc-container">
                    <h1 className="brand-name">{t('common:app.name')}</h1>
                    <span className="brand-desc">{t('common:app.banner')}</span>
                  </motion.div>
                }
              </AnimatePresence>
            </Flex>
          </Col>
          {!envConfig.ui.removeCorpInfo &&
            <div className="app-login-footer">
              <a target="_blank" href="https://dadekavan.ir" rel="noreferrer">
                {t('common:app.owner-name-full')}
              </a>
            </div>
          }
        </Row>
      </div>
    </ConfigProvider>
  );
}

export default LoginPage;
